/**
 * Typed API client.
 *
 * One place that knows how to talk to the backend, so components never deal
 * with headers, error shapes or query-string assembly. Three deliberate
 * behaviours:
 *
 *  - the bearer token is fetched from the auth service and cached until it is
 *    close to expiry, then refreshed transparently;
 *  - a 401 clears the token and retries once, which covers the common case of a
 *    token expiring mid-session;
 *  - domain errors arrive as `ApiError` carrying the backend's stable `code`,
 *    `message` and `details`, so a screen can react to `workflow_error` without
 *    string-matching prose.
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const AUTH_URL = (import.meta.env.VITE_AUTH_URL as string | undefined) ?? "/auth";

/**
 * Absolute form of a configured URL.
 *
 * `fetch` is happy with a relative path, but better-auth's client parses its
 * base URL with `new URL()` and throws at import time if there is no protocol -
 * which blanks the whole page rather than failing a request. Relative paths are
 * the better default (they follow whatever origin the app is served from,
 * whether that is the Vite dev server or the gateway), so they are resolved here
 * instead of being banned.
 */
export function absoluteUrl(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window === "undefined") return value;
  return new URL(value, window.location.origin).toString().replace(/\/$/, "");
}

const AUTH_BASE_URL = absoluteUrl(AUTH_URL);

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-level messages from a 422, ready to attach to form inputs. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details.fields as { field: string; message: string }[] | undefined;
    if (!fields) return {};
    return Object.fromEntries(fields.map((entry) => [entry.field, entry.message]));
  }

  /** Unmet workflow conditions, when the backend refused an approval. */
  get blockingConditions(): string[] {
    return (this.details.conditions as string[] | undefined) ?? [];
  }
}

type TokenCache = { token: string; expiresAt: number };
let tokenCache: TokenCache | null = null;
let inFlight: Promise<string | null> | null = null;

/**
 * Exchange the session cookie for a short-lived JWT.
 *
 * Concurrent callers share one request: on a dashboard that fires eight queries
 * at once, this is the difference between one token exchange and eight.
 */
async function getToken(): Promise<string | null> {
  const now = Date.now();
  if (tokenCache && tokenCache.expiresAt > now + 30_000) {
    return tokenCache.token;
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await fetch(`${AUTH_URL}/token`, {
        method: "GET",
        credentials: "include",
        headers: { accept: "application/json" },
      });
      // A token exchange that fails because the auth service is busy or restarting is
      // not a signed-out session, and treating it as one is how somebody working gets
      // thrown back to the sign-in screen. Returning null here would drop the request
      // to cookie authentication, which hits the same struggling service again; better
      // to say what happened and let the caller retry.
      if (response.status === 429 || response.status >= 500) {
        throw new ApiError(
          503,
          "auth_unavailable",
          "The authentication service is busy. Retrying shortly.",
        );
      }
      if (!response.ok) return null;
      const body = (await response.json()) as { token?: string };
      if (!body.token) return null;
      // The plugin issues 30-minute tokens; cache for 25 to stay clear of the
      // boundary without decoding the JWT.
      tokenCache = { token: body.token, expiresAt: now + 25 * 60_000 };
      return body.token;
    } catch (caught) {
      if (caught instanceof ApiError) throw caught;
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function clearToken(): void {
  tokenCache = null;
}

export type QueryValue = string | number | boolean | null | undefined | string[];

function buildQuery(params?: Record<string, QueryValue>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      // Repeated keys, which is what FastAPI expects for list parameters.
      value.forEach((item) => search.append(key, item));
    } else {
      search.append(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  params?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Set for file uploads: the body is passed through untouched. */
  formData?: FormData;
  /** Return the raw response instead of parsed JSON (PDF downloads). */
  raw?: boolean;
}

async function execute<T>(path: string, options: RequestOptions, retry = true): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = { accept: "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const response = await fetch(`${API_URL}${path}${buildQuery(options.params)}`, {
    method: options.method ?? "GET",
    credentials: "include",
    headers,
    body,
    signal: options.signal,
  });

  if (response.status === 401 && retry) {
    clearToken();
    return execute<T>(path, options, false);
  }

  if (options.raw) {
    if (!response.ok) throw await toError(response);
    return response as unknown as T;
  }

  if (response.status === 204) return undefined as T;
  if (!response.ok) throw await toError(response);
  return (await response.json()) as T;
}

async function toError(response: Response): Promise<ApiError> {
  let code = "http_error";
  let message = response.statusText || "Request failed";
  let details: Record<string, unknown> = {};
  try {
    const body = (await response.json()) as {
      code?: string;
      message?: string;
      detail?: string;
      details?: Record<string, unknown>;
    };
    code = body.code ?? code;
    message = body.message ?? body.detail ?? message;
    details = body.details ?? {};
  } catch {
    // A non-JSON error body (a gateway timeout page, for instance) is left as is.
  }
  if (response.status === 401) {
    message = "Your session has ended. Please sign in again.";
  }
  return new ApiError(response.status, code, message, details);
}

export const api = {
  get: <T>(path: string, params?: Record<string, QueryValue>, signal?: AbortSignal) =>
    execute<T>(path, { method: "GET", params, signal }),
  post: <T>(path: string, body?: unknown, params?: Record<string, QueryValue>) =>
    execute<T>(path, { method: "POST", body, params }),
  put: <T>(path: string, body?: unknown, params?: Record<string, QueryValue>) =>
    execute<T>(path, { method: "PUT", body, params }),
  patch: <T>(path: string, body?: unknown, params?: Record<string, QueryValue>) =>
    execute<T>(path, { method: "PATCH", body, params }),
  del: <T>(path: string, params?: Record<string, QueryValue>) =>
    execute<T>(path, { method: "DELETE", params }),
  upload: <T>(path: string, formData: FormData, params?: Record<string, QueryValue>) =>
    execute<T>(path, { method: "POST", formData, params }),
  /** Fetch a PDF (or any blob) with authentication applied. */
  blob: async (path: string, params?: Record<string, QueryValue>): Promise<Blob> => {
    const response = await execute<Response>(path, { method: "GET", params, raw: true });
    return response.blob();
  },
};

export { API_URL, AUTH_URL, AUTH_BASE_URL };
